import { extractScSd, getBasePriceFromIncludedPrice, getParsedNumber, getStoredFoodsFromSession, isSameTwoArrays, roundNumber, validatePhoneNumber } from "../utils/Common";
import { message } from "antd";
import {
  formatAsOrderListFromOrderDetails,
  getFormatedFoodItems,
  getTotalPriceOfSelectedItems,
} from "./order-common-service";
import { getSelectedTablesFromStore } from "../utils/store-mutator.table-management";

export const defaultInputs = {
  countryCode: "880",
  mobileNo: "",
  tableNo: "",
  guestNo: "",
  preparingTime: "",
  orderType: "",
  takeoutType: "",
  specialInstruction: "",
};

export const getInitFormInputs = (context, order, isTakeout, user, loyaltySettings) => {
  let mobileNo = "";

  if (loyaltySettings.isActive && context === 0) {
    mobileNo = "";
  } else if (loyaltySettings.isActive && context === 1) {
    mobileNo = order.phone;
  } else {
    mobileNo = order.phone || user.phone || "";
  }

  return {
    ...defaultInputs,
    mobileNo,
    tableNo: order.tableNumber || "",
    guestNo: order.guests || "1",
    preparingTime: order.cookingTime || "30",
    orderType: isTakeout ? "Takeout" : "Dine-in",
    takeoutType: order.takeoutType || "",
    specialInstruction: order.comment || "",
  };
};

export const getQueryParamsOrderForm = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const tableManagement = queryParams.get("tablemanagement");
  const takeout = queryParams.get("takeout");

  return {
    isTableManagement: tableManagement === "Active",
    isTakeout: takeout === "Yes",
    orderPageNo: queryParams.get("pageNo"),
    sourcePage: queryParams.get("sourcePage"),
    paymentStatus: getParsedNumber(queryParams.get("paymentStatus")),
  };
};

export const getIdWiseFoods = (allFoods) => {
  const foods = {};

  allFoods.forEach((item) => {
    foods[item.id] = item;
  });

  return foods;
};

/**
 * The main purpose is to have all addition groups in ordered food items from original food items. In ordered food item, addition groups will have isSelected property
 * and addition items will have ordered quantity from ordered addition items
 *
 * @param {*} idWiseAllFoods
 * @param {*} orderedItems
 * @returns
 */
export const getFormatedAdditionGroupsOrderEdit = (idWiseAllFoods, orderRecord) => {
  const idWiseItems = {};

  orderRecord.foods = orderRecord.foods.map((foodItem) => {
    const food = idWiseAllFoods[foodItem.foodId];
    let allAdditionGroups = [...food.additionGroups];
    const orderItemAdditionGroups = [...foodItem.additionGroups];

    // Just to quick access of addition groups and their items
    orderItemAdditionGroups.forEach((oiag) => {
      idWiseItems[oiag.additionGroupId] = oiag;
      oiag.additionItems.forEach((oiagItem) => {
        idWiseItems[`${oiag.additionGroupId}_${oiagItem.additionItemId}`] = oiagItem;
      });
    });

    allAdditionGroups = allAdditionGroups.map((groupItem) => {
      groupItem["isSelected"] = idWiseItems.hasOwnProperty(groupItem.additionGroupId);
      groupItem.additionItems = groupItem.additionItems.map((additionItem) => {
        const key = `${groupItem.additionGroupId}_${additionItem.additionItemId}`;
        return {
          ...additionItem,
          quantity: idWiseItems.hasOwnProperty(key) ? idWiseItems[key].quantity : 0,
        };
      });

      groupItem["totalPriceOfSelectedItems"] = getTotalPriceOfSelectedItems(
        groupItem,
        groupItem.additionItems,
        foodItem.quantity
      );

      return groupItem;
    });

    return {
      ...foodItem,
      additionGroups: allAdditionGroups,
    };
  });

  return orderRecord;
};

export const makeCompatibleAdditionGroups = (groups) => {
  const additionGroups = [...groups];

  return additionGroups.map((item) => {
    item.isSelected = false;
    item.totalPriceOfSelectedItems = 0;
    item.additionItems = item.additionItems.map((aItem) => ({ ...aItem, quantity: 0 }));

    return item;
  });
};

export const areAdditionGroupsIdentical = (groupsA, groupsB) => {
  // Filter only selected groups
  const selectedA = groupsA.filter((g) => g.isSelected);
  const selectedB = groupsB.filter((g) => g.isSelected);

  // If number of selected groups is not same, they are different
  if (selectedA.length !== selectedB.length) return false;

  // Compare each group
  return selectedA.every((groupA) => {
    // Find the matching group in B
    const groupB = selectedB.find((g) => g.additionGroupId === groupA.additionGroupId);
    if (!groupB) return false;

    // Compare items
    if (groupA.additionItems.length !== groupB.additionItems.length) return false;

    return groupA.additionItems.every((itemA) => {
      const itemB = groupB.additionItems.find((i) => i.additionItemId === itemA.additionItemId);
      return itemB && itemB.quantity === itemA.quantity;
    });
  });
};

export const getAdditionGroupsBreakdown = (additionGroups, foodQuantity) => {
  return (
    additionGroups
      // Only consider selected groups
      .filter((group) => group.isSelected)
      .map((group) => {
        let remainingFree = group.numberOfFreeItems * foodQuantity; // track free items left
        const additionItemsFree = [];
        const additionItemsPaid = [];

        group.additionItems.forEach((item) => {
          if (item.quantity <= 0) return;

          // If we still have free items left
          if (remainingFree > 0) {
            // Calculate how many can go free
            const freeQty = Math.min(item.quantity, remainingFree);
            additionItemsFree.push({
              ...item,
              quantity: freeQty,
            });
            remainingFree -= freeQty;

            // Remaining items (if any) go to paid
            if (item.quantity > freeQty) {
              additionItemsPaid.push({
                ...item,
                quantity: item.quantity - freeQty,
              });
            }
          } else {
            // All items are paid now
            additionItemsPaid.push({ ...item });
          }
        });

        return {
          ...group,
          additionItemsFree,
          additionItemsPaid,
        };
      })
  );
};

export const getUuidForVariantChanged = (foodItem) => {
  const orderedFoods = getStoredFoodsFromSession();
  let fuid = foodItem.fuid;

  const orderItem = orderedFoods.find(item => item.fuid === foodItem.fuid);
  // return newly added food's item fuid exactly
  if (orderItem === undefined) {
    return fuid;
  }

  // Unchanged option
  if (orderItem.optionId === foodItem.optionId) {
    return fuid;
  }

  return crypto.randomUUID();
}

export const getFormatedFoodsBeforeSubmit = (foods) => {
  return foods.map((cartItem) => {
    return {
      ...cartItem,
      // uid: cartItem.fuid,
      uid: getUuidForVariantChanged(cartItem),
      addOns: cartItem.addOns.map((addOn) => ({ addOnId: addOn.id })),
      additionGroups: cartItem.additionGroups
        .filter((groupItem) => groupItem.isSelected)
        .map((group) => {
          return {
            additionGroupId: group.additionGroupId,
            totalPriceOfSelectedItems: getTotalPriceOfSelectedItems(group, group.additionItems, cartItem.quantity),
            additionItems: group.additionItems.filter((additionItem) => additionItem.quantity > 0),
          };
        }),
    };
  });
};

export const restoreStockOnOrderEdit = (allFoods, order) => {
  if (order.paymentStatus === 1) {
    return allFoods;
  }

  const mergedFoods = [...order.foods];

  const foodList = [...allFoods];

  mergedFoods.forEach((item) => {
    const totalRestoringStock = item.quantity * item.weight;

    const indx = foodList.findIndex((food) => item.foodId === food.id);
    foodList[indx].availableQuantity += totalRestoringStock;
  });

  return foodList;
};

export const getFoodUnitPrice = (foodId, optionId, availableFoods) => {
  if (!foodId || !optionId) {
    return 0;
  }

  const foods = availableFoods || [];

  const food = foods.find((item) => item.id === foodId);

  if (food === undefined) {
    return 0;
  }

  const option = food.options.find((item) => item.optionId === optionId);

  if (option === undefined) {
    return 0;
  }

  return option.currentPrice;
};

export const getItemTotalAmount = (qty, price) => {
  const qnty = getParsedNumber(qty);
  const foodPrice = getParsedNumber(price);

  return (qnty * foodPrice).toFixed(2);
};

export const getPriceForAddOn = (availableFoods, foodId, addOnId) => {
  let targetFood = availableFoods.find((item) => item.id == foodId);
  let targetAddOn = null;
  if (Boolean(targetFood) && targetFood.addOns.length > 0) {
    targetAddOn = targetFood.addOns.find((item) => item.id == addOnId);
  }
  if (!Boolean(targetAddOn)) {
    return 0;
  }
  return targetAddOn.price;
};

export const getPriceForOption = (availableFoods, foodId, optionId) => {
  let targetFood = availableFoods.find((item) => item.id === foodId);
  let targetOption = null;
  if (Boolean(targetFood?.options) && targetFood?.options.length > 0) {
    targetOption = targetFood.options.find((item) => item.optionId === optionId);
  }

  if (Boolean(targetOption)) {
    return targetOption.currentPrice;
  }
  return 0;
};

export const getAdditionGroupPriceAsText = (group) => {
  if (group.numberOfFreeItems === 0 && getParsedNumber(group.totalPriceOfSelectedItems) === 0) {
    return `${getParsedNumber(group.pricePerAddition).toFixed(2)} TK`;
  }

  if (group.numberOfFreeItems > 0 && group.totalPriceOfSelectedItems === 0) {
    return "Free";
  }

  return `${getParsedNumber(group.totalPriceOfSelectedItems).toFixed(2)} TK`;
};

/** This recalculation needed as we are not updating the additionGroups for food quantity change */
export const recalculateTotalGroupPrice = (groups, foodQuantity) => {
  let total = 0;

  groups.forEach((group) => {
    if (!group.isSelected) {
      return;
    }

    total += getTotalPriceOfSelectedItems(group, group.additionItems, foodQuantity);
  });

  return total;
};

/**
 *
 * @param {*} foodItems It is cart items
 * @param {*} availableFoods it is all available foods
 * @returns
 */
export function calculateTotal(foodItems, availableFoods = []) {
  let total = 0;
  if (foodItems.length === 0) return;

  foodItems.map((foodItem) => {
    const addOnsPrice = foodItem.addOns.reduce((sum, addOn) => sum + addOn.price, 0);
    let foodPrice = foodItem.unitPrice + addOnsPrice;
    foodPrice = roundNumber(foodPrice * foodItem.quantity);
    total = roundNumber(total + foodPrice);

    // Calculate total price directly from additionGroups
    const totalAdditionGroupsPrice = recalculateTotalGroupPrice(foodItem.additionGroups, foodItem.quantity);

    total += totalAdditionGroupsPrice;
  });

  let sc = 0;
  let vat = 0;
  let isVatIncluded = false;

  // get restaurant vat and service charge from state
  const authStorage = localStorage.getItem("auth-storage");

  if (authStorage) {
    const tmp = JSON.parse(authStorage);
    vat = getParsedNumber(tmp.state.restaurantVat);
    sc = getParsedNumber(tmp.state.restaurantServiceCharge);
    isVatIncluded = tmp.state.isVatIncluded;
  }

  const calculatedSc = roundNumber((total * sc) / 100);
  const netTotal = roundNumber(total + calculatedSc);
  const calculatedVat = roundNumber((netTotal * vat) / 100);

  return {
    total,
    calculatedVat,
    calculatedSc,
    actualTotal: isVatIncluded ? total : roundNumber(netTotal + calculatedVat),
  };
}
const getScForVatNotApplicable = (totalFoodPrice, scPercent, sdPercent) => {
  const priceObj = extractScSd(totalFoodPrice, scPercent, sdPercent);

  console.log('--------getScForVatNotApplicable--------', {totalFoodPrice, sdPercent, scPercent, priceObj});

  return priceObj.serviceCharge;
}

export const getFoodWiseData = (foodItem, isVatIncluded, isSdIncluded, isScIncluded, scPercent) => {
  let addOnsPrice = foodItem.addOns.reduce((sum, addOn) => sum + addOn.price, 0);
  addOnsPrice = addOnsPrice * foodItem.quantity;
  const totalAdditionGroupsPrice = recalculateTotalGroupPrice(foodItem.additionGroups, foodItem.quantity);
  let foodPrice = roundNumber((foodItem.unitPrice * foodItem.quantity) + addOnsPrice);
  foodPrice = roundNumber(foodPrice + totalAdditionGroupsPrice);

  if (isVatIncluded) {
    return {
      foodPrice,
      vatApplicablePrice: 0,
      scApplicablePrice: 0,
      sd: 0,
    };
  }

  let vatApplicablePrice = 0;
  let scApplicablePrice = 0;
  let sd = 0;

  /**
   * Vat Excluded has following SD cases
   *          -> Vat not applicable
   *          -> Vat applicable
   *          -> SD applicable
   *          -> SD not applicable
   * Case 1: SD Included sc included
   *======I> In this Case SD always be zero and vatApplicablePrice will be the food price for vat applicable food (sd applicable or not no need to check) 
   * Case 2: SD Included sc excluded: here sc will applied to only food price not whole food price
   *======I> In this Case return sd will be zero but need to get food price without sd and vatApplicablePrice will be the food price for vat applicable food (SD Applicable or not no need to check as food price consist of SD)
   * Case 3: SD excluded sc excluded
   *========1> SD will have a value if SD Applicable
   *========2> vat applicable price will have two case
   ==============I> only food price when SD not applicable
   ==============I> food price + sd when sd applicable
   * Case 4: SD excluded sc included
   *========1> SD will have a value from only food price if SD Applicable
   *========2> vat applicable price will have two case
   ==============I> only food price when SD not applicable
   ==============I> food price + sd when sd applicable
   */
  // ===> SD included Cases 1 and 2 zero SD will be added to total sd, so SD applicable no need to check
  //isSdIncluded && !foodItem.isVatApplicable not need to handle it as vatApplicablePrice and sd will be zero
  // case 1
  if (isSdIncluded) {
    if (isScIncluded) {
      scApplicablePrice = 0;
      vatApplicablePrice = foodItem.isVatApplicable ? foodPrice : getScForVatNotApplicable(foodPrice, scPercent, foodItem.supplementaryDuty);

    } else {
      let withoutSdFoodPrice = foodPrice;
      if (foodItem.supplementaryDuty > 0) {
        // foodPriceWithoutSd = roundNumber((foodPriceWithoutSd * foodItem.quantity) + addOnsPrice);
        // withoutSdFoodPrice = roundNumber(foodPriceWithoutSd + totalAdditionGroupsPrice);
        
        withoutSdFoodPrice = getBasePriceFromIncludedPrice(foodPrice, foodItem.supplementaryDuty);
      }

      scApplicablePrice = withoutSdFoodPrice;
      vatApplicablePrice = foodItem.isVatApplicable ? foodPrice : 0; 
    }
    

    return {
      foodPrice,
      vatApplicablePrice,
      scApplicablePrice,
      sd: 0
    };
  }

  // Case 3, 4 SD should be added to total sd if sd applicable
  if (!isSdIncluded) {
    if (isScIncluded) {
      scApplicablePrice = 0;
      const foodPriceWithoutSc = getBasePriceFromIncludedPrice(foodPrice, scPercent);
      sd = (foodPriceWithoutSc * foodItem.supplementaryDuty) / 100;
      vatApplicablePrice = foodItem.isVatApplicable ? foodPrice + sd : foodPrice - foodPriceWithoutSc;

      console.log('==!isSdIncluded===isScIncluded==', {
        vatApplicablePrice,
        foodPrice,
        scPercent,
        supplementaryDuty: foodItem.supplementaryDuty,
        foodId: foodItem.foodId
      });

    } else {
      scApplicablePrice = foodPrice;
      sd = (foodPrice * foodItem.supplementaryDuty) / 100;
      vatApplicablePrice = foodItem.isVatApplicable ? foodPrice + sd : 0
    }
    
    return {
      foodPrice,
      vatApplicablePrice,
      scApplicablePrice,
      sd
    };
  }

  console.log('************This line should not executed. If executed then there have a problem.*************');
  return {
      foodPrice: 0,
      vatApplicablePrice: 0,
      scApplicablePrice: 0,
      sd: 0
  };
  
};

export function calculateSummary(foodItems) {
  if (foodItems.length === 0) {
    return {
      total: 0,
      calculatedSd: 0,
      calculatedSc: 0,
      actualTotal: 0,
    };
  }

  let isVatIncluded = true;
  let isSdIncluded = true;
  let isScIncluded = true;
  let vatPercent = 0;
  let scPercent = 0;

  const authStorage = localStorage.getItem("auth-storage");

  if (authStorage) {
    const tmp = JSON.parse(authStorage);
    vatPercent = getParsedNumber(tmp.state.restaurantVat);
    scPercent = getParsedNumber(tmp.state.restaurantServiceCharge);
    isVatIncluded = tmp.state.isVatIncluded;
    isSdIncluded = tmp.state.isSupplementaryDutyIncluded;
    isScIncluded = tmp.state.isServiceChargeIncluded;
  }

  let totalFoodPrice = 0;
  let totalVatApplicable = 0;
  let totalScApplicable = 0;
  let totalSd = 0;

  foodItems.map((foodItem) => {
    const calcValues = getFoodWiseData(foodItem, isVatIncluded, isSdIncluded, isScIncluded, scPercent);
    totalFoodPrice = totalFoodPrice + calcValues.foodPrice;
    totalScApplicable = totalScApplicable + calcValues.scApplicablePrice;
    totalVatApplicable = totalVatApplicable + calcValues.vatApplicablePrice;
    totalSd = totalSd + calcValues.sd;
  });

  if (isVatIncluded) {
    return {
      total: totalFoodPrice,
      calculatedSd: 0,
      calculatedSc: 0,
      calculatedVat: 0,
      actualTotal: totalFoodPrice,
    };
  }
  /** Vat excluded, sd or sc can be included or excluded, vat can be applicable, sd can be applicable */
  const calculatedSc = roundNumber((totalScApplicable * scPercent) / 100);
  const netVatApplicable = totalVatApplicable + calculatedSc;
  const calculatedVat = roundNumber((netVatApplicable * vatPercent) / 100);

  return {
    total: totalFoodPrice,
    calculatedSd: totalSd,
    calculatedSc,
    calculatedVat,
    actualTotal: roundNumber(totalFoodPrice + calculatedSc + totalSd + calculatedVat),
  };
}

function formatAddOns(addOns) {
  const tempAddOns = [];
  if (addOns.length > 0 && addOns[0].addOnId !== "") {
    addOns.map((adOn) => {
      if (adOn.hasOwnProperty("name")) {
        tempAddOns.push({ addOnId: adOn.addOnId });
      } else {
        tempAddOns.push({ addOnId: adOn });
      }
    });
  }
  return tempAddOns;
}

function getFoodType(foodId, availableFoods) {
  if (foodId === "") {
    message.warning("Please select food first.");
    return;
  }

  const food = availableFoods.find((food) => food.id === foodId);

  return food?.foodType;
}

const getItemAddOnsForListView = (itemAddOns) => {
  const addOns = [];
  itemAddOns.forEach((item) => {
    if (typeof item === "string") {
      addOns.push(item);
    } else {
      addOns.push(item.addOnId);
    }
  });

  return addOns;
};

export const mergeIdenticalFoodItemsGrid = (foodItems) => {
  console.log("*************mergeIdenticalFoodItemsGrid***********", { foodItems });
  const merged = [];

  for (const item of foodItems) {
    const matchIndex = merged.findIndex((existing) => {
      const isSame =
        existing.foodId === item.foodId && existing.optionId === item.optionId && !item.hasAddOnsOrMealAdditions;

      return isSame;
    });

    if (matchIndex !== -1) {
      merged[matchIndex].quantity += item.quantity;
      merged[matchIndex].additionGroups = getMergedAdditionGroups(item.additionGroups);
    } else {
      merged.push({
        ...item,
        addOns: [...item.addOns],
      });
    }
  }

  return merged;
};

/**
 * Returns a new array of additionGroups with doubled quantities and totalPriceOfSelectedItems
 */
export function getMergedAdditionGroups(groups) {
  return groups.map((group) => {
    if (!group.isSelected) return group;

    const updatedItems = group.additionItems.map((item) => ({
      ...item,
      quantity: item.quantity * 2,
    }));

    return {
      ...group,
      additionItems: updatedItems,
      totalPriceOfSelectedItems: roundNumber(parseFloat(group.totalPriceOfSelectedItems) * 2).toFixed(2),
    };
  });
}

/**
 * Currently it is only used for list view order but it can be used for grid view also by making food items compatible or same format
 *
 * @param {*} foodItems
 * @returns
 */
export function mergeIdenticalFoodItems(foodItems) {
  const merged = [];

  for (const item of foodItems) {
    const matchIndex = merged.findIndex((existing) => {
      const itemAddOns = getItemAddOnsForListView(item.addOns);

      return (
        existing.foodId === item.foodId &&
        existing.optionId === item.optionId &&
        isSameTwoArrays(existing.addOns, itemAddOns)
      );
    });

    if (matchIndex !== -1) {
      merged[matchIndex].quantity += item.quantity;
    } else {
      const tmpAddOns = getItemAddOnsForListView(item.addOns);
      merged.push({
        ...item,
        addOns: [...tmpAddOns],
      });
    }
  }

  return merged;
}

export function formatFoodItems(foodItems, availableFoods) {
  const _foods = [];
  foodItems.map((item) => {
    const formattedAddOns = formatAddOns(item.addOns);
    const obj = {
      foodType: getFoodType(item.foodId, availableFoods),
      categoryId: item.categoryId,
      foodId: item.foodId,
      optionId: item.optionId,
      addOns: formattedAddOns,
      quantity: item.quantity,
      comment: item.comment,
    };
    _foods.push(obj);
  });
  return _foods;
}

export const validateAndGetTableNumber = () => {
  const { isTakeout, isTableManagement } = getQueryParamsOrderForm();

  if (!isTableManagement) {
    return {
      errorMsg: "",
      tables: [],
    };
  }

  if (isTakeout) {
    return {
      errorMsg: "",
      tables: [],
    };
  }

  const selectedTables = getSelectedTablesFromStore();

  return {
    errorMsg: selectedTables.length === 0 ? "Table is required" : "",
    tables: selectedTables,
  };
};

export function validateOrder(context, countryCode, phone, foodItems, cookingTime) {
  if (countryCode === "") {
    return "Please add country code.";
  }

  if (phone === "") {
    return "Please add phone number.";
  }

  if (!validatePhoneNumber(phone)) {
    return "Please, provide a valid phone number";
  }

  if (context === 0 && !cookingTime) {
    return "Please, provide food preparing time.";
  }

  if (foodItems.length === 0) {
    return "You need to select at least 1 food item.";
  }

  if (foodItems.length > 0) {
    if (foodItems[0].foodId === "" || foodItems[0].optionId === "") {
      return "Please select your food and food option.";
    }
  }

  if (foodItems.length > 1) {
    foodItems.map((item, idx) => {
      if (item.foodId === "" || item.optionId === "") {
        return `Please select your ${idx + 1} food and food option.`;
      }
    });
  }

  return "";
}

export function isStockAvailable(foodId, optionId, quantity, availableFoods) {
  const authStorage = JSON.parse(localStorage.getItem("auth-storage"));
  const { inventoryType } = authStorage.state;

  if (inventoryType === "INGREDIENT") {
    return true;
  }

  let totalFoodQuantity = 0;
  if (foodId == null || foodId == "") {
    message.warning("Please select food first.");
    return;
  }

  const currentFood = availableFoods.find((food) => food.id === foodId);
  const currentOption = currentFood.options.find((option) => option.optionId === optionId);
  totalFoodQuantity += currentOption.weight * quantity;

  if (currentFood.availableQuantity < totalFoodQuantity) {
    return false;
  }

  return true;
}

/** It is currently to compare only single food items */
export function checkAndGetExistingCartItem(cartItems, foodId, selectedOptionId, selectedAddons = [], additionGroups) {
  let existingCartItem = null;

  for (let i = 0; i < cartItems.length; i++) {
    const cartItem = cartItems[i];

    const areTwoGroupsIdentical = areAdditionGroupsIdentical(cartItem.additionGroups, additionGroups);

    if (
      cartItem.foodId === foodId &&
      cartItem.optionId === selectedOptionId &&
      isSameTwoArrays(selectedAddons, cartItem.addOns) &&
      areTwoGroupsIdentical
    ) {
      existingCartItem = cartItem;
    }
  }

  return existingCartItem;
}

export const getExpectedItemFromCartItems = (cartItems, foodItem) => {
  if (cartItems.length === 0 || !foodItem) return { quantity: 1, additionGroups: [] };

  return cartItems.find((f) => f.fuid === foodItem.fuid);
};

export const prepareSnapshotPostSubmit = (order, foods) => {
  const compatibleFoods = foods.map((item) => {
    return {
      ...item,
      additionGroups: item.additionGroups
        .filter((groupItem) => groupItem.isSelected)
        .map((group) => {
          return {
            ...group,
            totalPriceOfSelectedItems: getTotalPriceOfSelectedItems(group, group.additionItems, item.quantity),
            additionItems: group.additionItems.filter((additionItem) => additionItem.quantity > 0),
          };
        }),
    };
  });

  return {
    ...order,
    foods: compatibleFoods,
  };
};

export const prepareSnapshotPreSubmit = () => {};
